import { z } from 'zod';
import { CuidSchema, ListQuerySchema } from './crm.schemas.js';
import {
  AGENT_STATES_MANUAL,
  CALL_STATUSES,
  MONITOR_MODES,
  QA_CRITERIA,
  SCREENSHOT_MAX_SEC,
  SCREENSHOT_MIN_SEC,
  TELEPHONY_PROVIDERS,
  TEMPLATE_CHANNELS,
} from './callcenter.js';

/**
 * مخططات مركز الاتصال.
 */

/* ==========================================================================
   المكالمة
   ========================================================================== */

/** رقم هاتف كما يُدخله الموظف — التطبيع في الخادم عبر كتالوج الهواتف. */
const RawPhoneSchema = z
  .string()
  .trim()
  .min(5, 'رقم أقصر من أن يكون هاتفًا.')
  .max(20, 'رقم أطول من أن يكون هاتفًا.')
  .regex(/^[+\d][\d\s\-()]*$/, 'أرقام فقط، ويجوز + في أوله.');

/**
 * بدء مكالمة.
 *
 * الربط بالسجلات اختياري كله: الرقم وحده يكفي للبدء، والخادم يبحث عمّن
 * يملكه — فتعريفُ المتصل عملُ النظام لا عبءٌ على الموظف.
 */
export const StartCallSchema = z.object({
  direction: z.enum(['OUTBOUND', 'INBOUND']),
  phone: RawPhoneSchema,
  leadId: CuidSchema.nullable().optional(),
  accountId: CuidSchema.nullable().optional(),
  contactId: CuidSchema.nullable().optional(),
  dealId: CuidSchema.nullable().optional(),
  /**
   * مرشّحٌ في التوظيف.
   *
   * وحين يُرسَل يُلغى بحثُ الخادم عن صاحب الرقم: مرشّحٌ رقمُه رقمُ عميلٍ
   * قديمٍ أو يشترك معه في آخر تسعة أرقامٍ كان يُكتب في ملفّ ذاك العميل.
   */
  candidateId: CuidSchema.nullable().optional(),
  /** النشاط (المهمة) الذي انطلقت منه المكالمة — يُغلق حلقة «اتصل بالعميل». */
  activityId: CuidSchema.nullable().optional(),
});
export type StartCallInput = z.infer<typeof StartCallSchema>;

/**
 * إنهاء مكالمة.
 *
 * النتيجة إلزامية: مكالمة بلا نتيجة سطرٌ لا تُبنى عليه متابعة ولا تقرير.
 * والفائتة وغير المجابة تُنهيان بحالتها والنتيجة تُترك للخادم.
 */
export const EndCallSchema = z.object({
  status: z.enum(CALL_STATUSES).refine((s) => s !== 'IN_PROGRESS', {
    message: 'الإنهاء لا يكون إلى «جارية».',
  }),
  dispositionId: CuidSchema.nullable().optional(),
  note: z.string().trim().max(4000).nullable().optional(),
  /** متابعة تُنشأ مهمةً فورًا: «كلّمني بعد ثلاثة أيام» تصير موعدًا لا ذكرى. */
  followUpAt: z.coerce.date().nullable().optional(),
  followUpSubject: z.string().trim().max(200).nullable().optional(),
});
export type EndCallInput = z.infer<typeof EndCallSchema>;

export const CallListQuerySchema = ListQuerySchema.extend({
  agentId: CuidSchema.optional(),
  status: z.enum(CALL_STATUSES).optional(),
  direction: z.enum(['OUTBOUND', 'INBOUND']).optional(),
  dispositionId: CuidSchema.optional(),
  leadId: CuidSchema.optional(),
  accountId: CuidSchema.optional(),
  candidateId: CuidSchema.optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});
export type CallListQuery = z.infer<typeof CallListQuerySchema>;

export const CallStatsQuerySchema = z.object({
  from: z.coerce.date(),
  to: z.coerce.date(),
  agentId: CuidSchema.optional(),
});
export type CallStatsQuery = z.infer<typeof CallStatsQuerySchema>;

/* ==========================================================================
   تقييم العميل — عبر الرابط العام
   ========================================================================== */

/**
 * ما يرسله العميل من صفحة التقييم العامة.
 *
 * نجومٌ من خمس وتعليقٌ قصير — لا أكثر: من يفتح رابطًا من هاتفه
 * لن يملأ استبيانًا من سبعة معايير، والمعايير السبعة شأن المشرف.
 */
export const SubmitRatingSchema = z.object({
  score: z.number().int().min(1, 'اختر تقييمًا.').max(5),
  /* درجات العناصر المضبوطة — المفاتيح تُنقّى في الخادم على قائمة الإعدادات. */
  scores: z
    .record(z.string().max(40), z.number().int().min(1).max(5))
    .optional(),
  comment: z.string().trim().max(1000).optional(),
});
export type SubmitRatingInput = z.infer<typeof SubmitRatingSchema>;

/** ضبط عناصر سياقٍ واحد — حتى عشرة، والفارغة تعني نجومًا وتعليقًا فقط. */
export const RatingItemsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/, 'معرّف غير صالح.'),
        label: z.string().trim().min(2, 'الاسم قصير.').max(80),
      }),
    )
    .max(10, 'عشرة عناصر كحدٍّ أقصى.'),
});
export type RatingItemsInput = z.infer<typeof RatingItemsSchema>;

/** طلب رابط تقييم لكيانٍ غير المكالمة — العقود خرجت والمهام دخلت. */
export const RequestRatingSchema = z.object({
  kind: z.enum(['ACCOUNT', 'DEAL', 'PROJECT', 'TASK']),
  entityId: CuidSchema,
});
export type RequestRatingInput = z.infer<typeof RequestRatingSchema>;

/** رمز الرابط العام — hex طويل من الخادم؛ يُفحص شكله قبل أي استعلام. */
export const RatingTokenSchema = z.object({
  token: z.string().regex(/^[0-9a-f]{48}$/, 'الرابط غير صالح.'),
});

/* ==========================================================================
   النتائج — كتالوج تحرّره الإدارة
   ========================================================================== */

export const UpsertDispositionSchema = z.object({
  label: z.string().trim().min(2, 'الاسم مطلوب.').max(60),
  kind: z
    .enum(['success', 'info', 'warning', 'danger', 'neutral'])
    .default('neutral'),
  suggestsFollowUp: z.boolean().default(false),
  isActive: z.boolean().default(true),
  order: z.number().int().min(0).max(999).default(0),
});
export type UpsertDispositionInput = z.infer<typeof UpsertDispositionSchema>;

/* ==========================================================================
   حالة الموظف
   ========================================================================== */

export const SetAgentStateSchema = z.object({
  /* «في مكالمة» يقلبها النظام مع بدء المكالمة وانتهائها — لا تُقبل من اليد. */
  state: z.enum(AGENT_STATES_MANUAL as unknown as [string, ...string[]]),
});
export type SetAgentStateInput = z.infer<typeof SetAgentStateSchema>;

/** خطّ الشركة المسلَّم للموظف — فارغًا يعني نزعه. */
export const SetAgentPhoneSchema = z.object({
  phone: RawPhoneSchema.nullable(),
});
export type SetAgentPhoneInput = z.infer<typeof SetAgentPhoneSchema>;

/** إعدادات متابعة العمل — الفارغ في الأسرار يعني «أبقِ المحفوظ». */
export const MonitorConfigSchema = z.object({
  /* التسجيل ثنائي: إمّا كل المكالمات أو لا. */
  callRecording: z.boolean().optional(),
  mode: z.enum(MONITOR_MODES).optional(),
  screenshotEverySec: z
    .number()
    .int()
    .min(SCREENSHOT_MIN_SEC)
    .max(SCREENSHOT_MAX_SEC)
    .optional(),
  /** مدة الاحتفاظ بالملتقَط قبل حذفه — يوم إلى تسعين. */
  retentionDays: z.number().int().min(1).max(90).optional(),
  /** أين يُخزَّن الثقيل: قاعدة البيانات أو Drive الشركة. */
  storage: z.enum(['DB', 'DRIVE']).optional(),
});
export type MonitorConfigInput = z.infer<typeof MonitorConfigSchema>;

/** استعلام سجلّ التقاطات المراقبة. */
export const MonitorCapturesQuerySchema = z.object({
  userId: CuidSchema.optional(),
  kind: z.enum(['SHOT', 'SEGMENT']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(40),
});
export type MonitorCapturesQuery = z.infer<typeof MonitorCapturesQuerySchema>;

/**
 * حذف اللقطات — إمّا معرّفاتٌ مختارة، وإمّا الكلُّ ضمن مرشِّح.
 *
 * `all` يحذف كلَّ ما يطابق `userId`/`kind` عبر الصفحات لا الصفحةَ الظاهرة
 * وحدَها: «حدّد الكلّ» في شاشةٍ فيها ألفُ لقطةٍ يجب أن يحذفها كلَّها لا
 * الأربعين المعروضة. وأحدُهما مطلوب: طلبٌ بلا معرّفاتٍ ولا `all` لا يحذف
 * شيئًا صامتًا.
 */
export const DeleteCapturesSchema = z
  .object({
    ids: z.array(CuidSchema).max(500).optional(),
    all: z.boolean().optional(),
    userId: CuidSchema.optional(),
    kind: z.enum(['SHOT', 'SEGMENT']).optional(),
  })
  .refine((v) => v.all === true || (v.ids?.length ?? 0) > 0, {
    message: 'حدّد لقطاتٍ للحذف أو اختر حذف الكل.',
    path: ['ids'],
  });
export type DeleteCapturesInput = z.infer<typeof DeleteCapturesSchema>;

/** المدير يطلب مشاهدة موظف حيًّا. */
export const WatchAgentSchema = z.object({ userId: CuidSchema });
export type WatchAgentInput = z.infer<typeof WatchAgentSchema>;

/** أعلام الجلسة يبلّغها المتصفح — تُعرض في لوحة الفريق. */
export const PresenceFlagsSchema = z.object({
  recordingOn: z.boolean().optional(),
  monitorOn: z.boolean().optional(),
});
export type PresenceFlagsInput = z.infer<typeof PresenceFlagsSchema>;

/** ربط تسجيلٍ مرفوع بالمكالمة. */
export const AttachRecordingSchema = z.object({ attachmentId: CuidSchema });
export type AttachRecordingInput = z.infer<typeof AttachRecordingSchema>;

/* ==========================================================================
   تقييم الجودة
   ========================================================================== */

const criterionKeys = QA_CRITERIA.map((c) => c.key);

export const ReviewCallSchema = z.object({
  /** درجة كل معيار من مئة — والدرجة الكلية يحسبها الخادم من الأوزان. */
  scores: z
    .record(z.string(), z.number().min(0).max(100))
    .refine((r) => Object.keys(r).every((k) => criterionKeys.includes(k)), {
      message: 'معيار غير معروف.',
    }),
  feedback: z.string().trim().max(4000).nullable().optional(),
});
export type ReviewCallInput = z.infer<typeof ReviewCallSchema>;

/* ==========================================================================
   إعدادات المزوّد والقوالب
   ========================================================================== */

export const TelephonyConfigSchema = z.object({
  provider: z.enum(TELEPHONY_PROVIDERS).default('NONE'),
  callerId: z.string().trim().max(25).nullable().optional(),
  accountRef: z.string().trim().max(120).nullable().optional(),
  /** فارغًا يعني «لا تغيّر السر المحفوظ» — كأسرار البريد. */
  secret: z.string().max(300).optional(),
  recordCalls: z.boolean().default(false),
});
export type TelephonyConfigInput = z.infer<typeof TelephonyConfigSchema>;

export const UpsertTemplateSchema = z.object({
  name: z.string().trim().min(2, 'الاسم مطلوب.').max(80),
  channel: z.enum(TEMPLATE_CHANNELS),
  subject: z.string().trim().max(200).nullable().optional(),
  body: z.string().trim().min(2, 'نص القالب مطلوب.').max(4000),
  isActive: z.boolean().default(true),
  order: z.number().int().min(0).max(999).default(0),
});
export type UpsertTemplateInput = z.infer<typeof UpsertTemplateSchema>;
